import React, { useState, useEffect, useMemo, useRef } from 'react';
import confetti from 'canvas-confetti';
import toast from 'react-hot-toast';
import { Chess } from 'chess.js';
import { doc, setDoc, arrayUnion, collection, addDoc, increment, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { PIECE_SETS, BOARD_THEMES, playSound } from '../components/Board/BoardConstants';
import { getTasksForClass, BOOTCAMP_CLASSES } from '../utils/kidsBootcampData';

const ROWS = [8, 7, 6, 5, 4, 3, 2, 1];
const COLS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

const parseFenToBoard = (fen) => {
    const board = {};
    if (!fen) return board;
    const rows = fen.split(' ')[0].split('/');
    rows.forEach((row, rIdx) => {
        let cIdx = 0;
        for (let char of row) {
            if (!isNaN(char)) cIdx += parseInt(char);
            else { 
                const color = char === char.toUpperCase() ? 'w' : 'b';
                board[`${COLS[cIdx]}${8 - rIdx}`] = color + char.toUpperCase(); 
                cIdx++; 
            }
        }
    });
    return board;
};

export default function KidsGameEngine({ assignment, myProfile, onBack }) {
    // Determine class ID from assignment levelId e.g. "KIDS_intro_board_1" or "KIDS_ranks_files_2" or "intro_board"
    const classId = useMemo(() => {
        if (!assignment?.levelId) return 1;
        const parts = assignment.levelId.split('_');
        const num = parseInt(parts[parts.length - 1]);
        if (!isNaN(num) && num >= 1 && num <= 36) return num;
        // Search by tag
        const tag = parts.slice(1, parts.length - 1).join('_');
        return parseInt(tag) || 1;
    }, [assignment]);

    const classObj = useMemo(() => {
        return BOOTCAMP_CLASSES.find(c => c.id === classId) || { name: "Unknown Class", icon: "❓" };
    }, [classId]);

    const tasks = useMemo(() => getTasksForClass(classId), [classId]);
    const [taskIndex, setTaskIndex] = useState(0);
    const activeTask = tasks[taskIndex];

    const [boardWidth, setBoardWidth] = useState(420);
    const [selectedSquare, setSelectedSquare] = useState(null);
    const [legalMoves, setLegalMoves] = useState([]);
    
    // Pointer drag and drop states
    const [dragSq, setDragSq] = useState(null);
    const [dragPos, setDragPos] = useState(null);
    const boardRef = useRef(null);
    const isDraggingRef = useRef(false);
    const pointerDownPosRef = useRef(null);
    const pointerDownOffsetRef = useRef({ x: 0, y: 0 });
    const justDraggedRef = useRef(false);
    
    // Task-specific states
    const [piecePos, setPiecePos] = useState('');
    const [applesOnBoard, setApplesOnBoard] = useState([]);
    const [completed, setCompleted] = useState(false);
    const [wrongAttempts, setWrongAttempts] = useState(0);
    const [mcqAnswered, setMcqAnswered] = useState(null);
    const [chessFen, setChessFen] = useState('');

    // Submission logging
    const [gameLogs, setGameLogs] = useState([]);
    const [currentTaskLogs, setCurrentTaskLogs] = useState([]);
    const [isProgressLoaded, setIsProgressLoaded] = useState(assignment?.isReplay ? true : false);

    const saveProgress = async (index, currentLogs, activeTaskLogs, isDone = false) => {
        if (assignment?.isReplay) return;
        if (!myProfile?.email || !assignment?.assignmentDocId || assignment.assignmentDocId === 'INDIVIDUAL') return;
        const safeEmail = myProfile.email.toLowerCase();
        const docId = `${assignment.assignmentDocId}_${safeEmail.replace(/[^a-zA-Z0-9]/g, '')}`;

        try {
            const currentTaskEntry = {
                taskNumber: index + 1,
                type: activeTask?.type || "",
                question: activeTask?.question || "",
                attempts: activeTaskLogs
            };

            const cleanLogs = [...currentLogs.filter(l => l.taskNumber !== index + 1), currentTaskEntry]
                .sort((a, b) => a.taskNumber - b.taskNumber)
                .map(log => ({
                    taskNumber: log.taskNumber || 0,
                    type: log.type || "",
                    question: log.question || "",
                    attempts: (log.attempts || []).map(att => ({
                        played: att.played || "",
                        isCorrect: !!att.isCorrect,
                        timestamp: att.timestamp || new Date().toISOString()
                    }))
                }));

            await setDoc(doc(db, "homework_submissions", docId), {
                homeworkId: assignment.assignmentDocId,
                levelId: assignment.levelId || '',
                homeworkTitle: assignment.title || 'Chess Mission',
                batchId: assignment.batchId || 'INDIVIDUAL',
                studentEmail: safeEmail,
                studentName: myProfile.name || safeEmail.split('@')[0] || "Student",
                submittedAt: new Date().toISOString(),
                currentIndex: index,
                totalPuzzles: 15,
                status: isDone ? 'completed' : 'in_progress',
                gameType: `BOOTCAMP_CLASS_${classId}`,
                logs: cleanLogs
            }, { merge: true });
        } catch (e) {
            console.error("Error saving kids bootcamp homework progress", e);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setBoardWidth(Math.min(window.innerHeight - 320, window.innerWidth - 40, 480));
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 🌐 GLOBAL DRAG-AND-DROP POINTER TRACKING (LICHESS STYLE)
    useEffect(() => {
        if (!dragSq) return;

        const handleGlobalPointerMove = (e) => {
            if (!pointerDownPosRef.current) return;
            const dx = e.clientX - pointerDownPosRef.current.x;
            const dy = e.clientY - pointerDownPosRef.current.y;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                isDraggingRef.current = true;
            }
            if (boardRef.current) {
                const rect = boardRef.current.getBoundingClientRect();
                setDragPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }
        };

        const handleGlobalPointerUp = (e) => {
            const targetSq = pixelToSquare(e.clientX, e.clientY);
            const wasDragMove = isDraggingRef.current && targetSq && targetSq !== dragSq;
            const draggedId = dragSq;

            if (isDraggingRef.current) {
                justDraggedRef.current = true;
                setTimeout(() => { justDraggedRef.current = false; }, 50);
            }

            // Clear drag states
            setDragSq(null);
            setDragPos(null);
            pointerDownPosRef.current = null;

            if (wasDragMove) {
                if (activeTask.type === 'real_chess') {
                    if (legalMoves.includes(targetSq)) {
                        handleChessMove(draggedId, targetSq);
                    }
                } else {
                    if (legalMoves.includes(targetSq)) {
                        handleMoveAttempt(draggedId, targetSq);
                    }
                }
            }
        };

        window.addEventListener('pointermove', handleGlobalPointerMove);
        window.addEventListener('pointerup', handleGlobalPointerUp);

        return () => {
            window.removeEventListener('pointermove', handleGlobalPointerMove);
            window.removeEventListener('pointerup', handleGlobalPointerUp);
        };
    }, [dragSq, legalMoves, activeTask]);

    // Fetch saved progress from Firestore on mount
    useEffect(() => {
        const loadSavedProgress = async () => {
            if (assignment?.isReplay) {
                setIsProgressLoaded(true);
                return;
            }
            if (myProfile?.email && assignment?.assignmentDocId && assignment.assignmentDocId !== 'INDIVIDUAL') {
                const safeEmail = myProfile.email.toLowerCase();
                const docId = `${assignment.assignmentDocId}_${safeEmail.replace(/[^a-zA-Z0-9]/g, '')}`;
                try {
                    const docSnap = await getDoc(doc(db, "homework_submissions", docId));
                    if (docSnap.exists()) {
                        const subData = docSnap.data();
                        if (subData.status === 'completed') {
                            setCompleted(true);
                            setGameLogs(subData.logs || []);
                            setTaskIndex(14); // Set to last task
                        } else {
                            const savedIndex = subData.currentIndex || 0;
                            setTaskIndex(savedIndex);
                            setGameLogs(subData.logs || []);
                        }
                    }
                } catch (e) {
                    console.error("Error loading saved progress in KidsGameEngine", e);
                }
            }
            setIsProgressLoaded(true);
        };
        loadSavedProgress();
    }, [assignment, myProfile]);

    // Load active task state
    useEffect(() => {
        if (!isProgressLoaded || !activeTask) return;
        setSelectedSquare(null);
        setLegalMoves([]);
        setWrongAttempts(0);
        setMcqAnswered(null);
        setCurrentTaskLogs([]);

        if (activeTask.type === 'collect_items') {
            setPiecePos(activeTask.startPos);
            setApplesOnBoard(activeTask.apples);
        } else if (activeTask.type === 'safe_squares') {
            setPiecePos(activeTask.startPos);
        } else if (activeTask.type === 'real_chess') {
            setChessFen(activeTask.fen);
        }

        // Save initial progress when activeTask loads
        saveProgress(taskIndex, gameLogs, [], false);
    }, [activeTask, taskIndex, isProgressLoaded]);

    if (!isProgressLoaded) {
        return (
            <div style={{color: 'var(--text-primary)', padding: '50px', textAlign: 'center', fontSize: '20px' }}>
                Loading Saved Progress... ⏳
            </div>
        );
    }

    if (!activeTask) {
        return (
            <div style={{color: 'var(--text-primary)', padding: '50px', textAlign: 'center' }}>
                <h2>No activities found for Class {classId}</h2>
                <button onClick={onBack} className="neu-btn-primary">Return</button>
            </div>
        );
    }

    const isMoveLegal = (from, to, pieceType) => {
        const fFile = from.charCodeAt(0) - 97; const fRank = parseInt(from[1]);
        const tFile = to.charCodeAt(0) - 97; const tRank = parseInt(to[1]);
        const dx = Math.abs(fFile - tFile);
        const dy = Math.abs(fRank - tRank);

        if (pieceType === 'Rook') return dx === 0 || dy === 0;
        if (pieceType === 'Bishop') return dx === dy;
        if (pieceType === 'Queen') return dx === 0 || dy === 0 || dx === dy;
        if (pieceType === 'King') return dx <= 1 && dy <= 1 && (dx > 0 || dy > 0);
        if (pieceType === 'Knight') return (dx === 1 && dy === 2) || (dx === 2 && dy === 1);
        if (pieceType === 'Pawn') {
            const forwardRank = fRank + (activeTask.startPos[1] === '2' ? 1 : 1); 
            // Pawns move forward straight, capture diagonally
            if (tRank !== fRank + 1) {
                // Initial 2 steps forward check
                if (from[1] === '2' && tRank === 4 && dx === 0) return !applesOnBoard.includes(to);
                return false;
            }
            if (dx === 0) return !applesOnBoard.includes(to);
            if (dx === 1) return applesOnBoard.includes(to);
            return false;
        }
        return false;
    };

    const getPieceMoves = (pos, pieceType) => {
        const moves = [];
        ROWS.forEach(r => COLS.forEach(c => {
            const sq = `${c}${r}`;
            if (sq !== pos && isMoveLegal(pos, sq, pieceType)) moves.push(sq);
        }));
        return moves;
    };

    const finishBootcamp = async (finalLogs) => {
        setCompleted(true);
        playSound('notify'); 
        confetti({ particleCount: 300, spread: 130, origin: { y: 0.5 } });

        if (assignment?.isReplay) return;

        if (myProfile && myProfile.email) {
            const safeEmail = myProfile.email.toLowerCase();
            const savingToast = toast.loading("Submitting graduation report... 🎓");
            
            try {
                const userRef = doc(db, 'users', safeEmail);
                await setDoc(userRef, { 
                    completedMissions: arrayUnion(assignment.id, assignment.levelId),
                    totalXp: increment(50)
                }, { merge: true });

                if (assignment.assignmentDocId && assignment.assignmentDocId !== 'INDIVIDUAL') {
                    const docId = `${assignment.assignmentDocId}_${safeEmail.replace(/[^a-zA-Z0-9]/g, '')}`;
                    const cleanLogs = finalLogs.map(log => ({
                        taskNumber: log.taskNumber || 0,
                        type: log.type || "",
                        question: log.question || "",
                        attempts: (log.attempts || []).map(att => ({
                            played: att.played || "",
                            isCorrect: !!att.isCorrect,
                            timestamp: att.timestamp || new Date().toISOString()
                        }))
                    }));

                    await setDoc(doc(db, "homework_submissions", docId), {
                        homeworkId: assignment.assignmentDocId,
                        levelId: assignment.levelId || '',
                        homeworkTitle: assignment.title,
                        batchId: assignment.batchId || 'INDIVIDUAL',
                        studentEmail: safeEmail,
                        studentName: myProfile.name || safeEmail.split('@')[0],
                        submittedAt: new Date().toISOString(),
                        currentIndex: 14,
                        totalPuzzles: 15,
                        status: 'completed',
                        gameType: `BOOTCAMP_CLASS_${classId}`,
                        logs: cleanLogs
                    }, { merge: true });
                    toast.success("Missions Cleared & Submitted to Coach! 🚀", { id: savingToast });
                } else {
                    toast.success("Class Cleared! +50 XP 🏆", { id: savingToast });
                }
            } catch (error) {
                console.error("Bootcamp finish error", error);
                toast.error("Error submitting progress!", { id: savingToast });
            }
        }
    };

    const handleTaskSuccess = (logUpdates) => {
        const updatedTaskLogs = [...currentTaskLogs, ...logUpdates];
        const newTotalLogs = [...gameLogs, {
            taskNumber: taskIndex + 1,
            type: activeTask.type,
            question: activeTask.question,
            attempts: updatedTaskLogs
        }];
        setGameLogs(newTotalLogs);
        
        playSound('notify');
        toast.success(taskIndex === 14 ? "Graduation Complete! 🎉" : "Perfect! Next Activity ⏩");

        // Save progress for this completed task (status: 'in_progress')
        saveProgress(taskIndex, gameLogs, updatedTaskLogs, false);

        if (taskIndex + 1 < tasks.length) {
            setTimeout(() => {
                setTaskIndex(taskIndex + 1);
            }, 1000);
        } else {
            finishBootcamp(newTotalLogs);
        }
    };

    const logWrongAttempt = (playedDetail) => {
        const logEntry = { played: playedDetail, isCorrect: false, timestamp: new Date().toISOString() };
        const updated = [...currentTaskLogs, logEntry];
        setCurrentTaskLogs(updated);
        setWrongAttempts(prev => prev + 1);
        playSound('error');
        saveProgress(taskIndex, gameLogs, updated, false);
    };

    // 🎯 Pointer Drag and Drop Logic
    const pixelToSquare = (clientX, clientY) => {
        if (!boardRef.current) return null;
        const rect = boardRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        if (x < 0 || y < 0 || x > boardWidth || y > boardWidth) return null;
        const sqSize = boardWidth / 8;
        const cIdx = Math.floor(x / sqSize);
        const rIdx = Math.floor(y / sqSize);
        if (cIdx < 0 || cIdx > 7 || rIdx < 0 || rIdx > 7) return null;
        return `${COLS[cIdx]}${ROWS[rIdx]}`;
    };

    const handlePointerDown = (e, id) => {
        const isMyPiece = boardPieces[id]?.startsWith('w');
        if (!isMyPiece) return;
        
        isDraggingRef.current = false;
        pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
        
        const rect = boardRef.current.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        
        const cIdx = COLS.indexOf(id[0]);
        const rIdx = ROWS.indexOf(parseInt(id[1]) || id[1]);
        const sqSize = boardWidth / 8;
        const pieceLeft = cIdx * sqSize;
        const pieceTop = rIdx * sqSize;
        pointerDownOffsetRef.current = { x: cursorX - pieceLeft, y: cursorY - pieceTop };
        
        setDragSq(id);
        setDragPos({ x: cursorX, y: cursorY });
        
        if (selectedSquare !== id) {
            setSelectedSquare(id);
            if (activeTask.type === 'collect_items') {
                setLegalMoves(getPieceMoves(id, activeTask.piece));
            } else if (activeTask.type === 'safe_squares') {
                setLegalMoves(getPieceMoves(id, 'King'));
            } else if (activeTask.type === 'real_chess') {
                try {
                    const tempGame = new Chess(chessFen);
                    setLegalMoves(tempGame.moves({ square: id, verbose: true }).map(m => m.to));
                } catch(e) { setLegalMoves([]); }
            } else {
                setLegalMoves([]);
            }
        }
    };

    // 🎯 Tap and Move Core Logic
    const handleSquareClick = (id) => {
        if (justDraggedRef.current) return;
        // Mode 1: Direct Grid Click (Intro to Board, Ranks/Files)
        if (activeTask.type === 'board_click' && activeTask.isDirectGridClick) {
            const isCorrect = activeTask.clickTargets.includes(id);
            if (isCorrect) {
                handleTaskSuccess([{ played: `Clicked square ${id.toUpperCase()}`, isCorrect: true }]);
            } else {
                logWrongAttempt(`Clicked square ${id.toUpperCase()}`);
                toast.error("Oops! Try a different square.");
            }
            return;
        }

        // Mode 2: Interactive pieces (Collect items & Safe squares)
        if (activeTask.type === 'collect_items' || activeTask.type === 'safe_squares') {
            const currentPiece = (activeTask.type === 'collect_items') ? activeTask.piece : 'King';
            const currentPos = piecePos;

            if (id === currentPos) {
                if (selectedSquare === id) {
                    setSelectedSquare(null);
                    setLegalMoves([]);
                } else {
                    setSelectedSquare(id);
                    setLegalMoves(getPieceMoves(id, currentPiece));
                }
                return;
            }

            if (selectedSquare === currentPos) {
                handleMoveAttempt(currentPos, id);
            }
        }
    };

    const handleMoveAttempt = (from, to) => {
        // Type 1: Collect Items
        if (activeTask.type === 'collect_items') {
            if (isMoveLegal(from, to, activeTask.piece)) {
                setPiecePos(to);
                setSelectedSquare(null);
                setLegalMoves([]);
                const isCapture = applesOnBoard.includes(to);
                const nextApples = applesOnBoard.filter(a => a !== to);
                setApplesOnBoard(nextApples);

                const logUpdates = [{ played: `Move ${activeTask.piece} from ${from} to ${to} ${isCapture ? '(Captured Star)' : ''}`, isCorrect: true }];

                if (isCapture) {
                    playSound('capture');
                } else {
                    playSound('move');
                }

                if (nextApples.length === 0) {
                    handleTaskSuccess(logUpdates);
                } else {
                    setCurrentTaskLogs(prev => [...prev, ...logUpdates]);
                }
            } else {
                logWrongAttempt(`Invalid move ${activeTask.piece} to ${to}`);
                toast.error(`Rooks slide straight, Knights jump Ls, Bishops diagonal!`);
            }
        }

        // Type 2: Safe Squares
        if (activeTask.type === 'safe_squares') {
            if (isMoveLegal(from, to, 'King')) {
                const isCorrect = (to === activeTask.targetSq);
                if (isCorrect) {
                    setPiecePos(to);
                    setSelectedSquare(null);
                    setLegalMoves([]);
                    handleTaskSuccess([{ played: `Moved King to safe square ${to}`, isCorrect: true }]);
                } else {
                    logWrongAttempt(`Moved King to dangerous square ${to}`);
                    toast.error("That square is under attack! Look for a safe space.");
                }
            } else {
                logWrongAttempt(`Invalid King step to ${to}`);
                toast.error("Kings can only move 1 step in any direction!");
            }
        }
    };

    // Mode 3: MCQ Quiz clicks
    const handleMcqClick = (option) => {
        const isCorrect = option === activeTask.answer;
        setMcqAnswered(option);
        if (isCorrect) {
            handleTaskSuccess([{ played: `Selected option: ${option}`, isCorrect: true }]);
        } else {
            logWrongAttempt(`Selected option: ${option}`);
            toast.error("That is incorrect. Try another option!");
        }
    };

    // Mode 4: Real Chess board drops (Chess.js moves)
    const handleChessMove = (from, to) => {
        try {
            const game = new Chess(chessFen);
            const move = game.move({ from, to, promotion: 'q' });
            if (move) {
                const isCorrect = (from === activeTask.correctMove.from && to === activeTask.correctMove.to);
                if (isCorrect) {
                    setChessFen(game.fen());
                    playSound('move');
                    handleTaskSuccess([{ played: `${move.san} [Mate move]`, isCorrect: true }]);
                } else {
                    logWrongAttempt(`${move.san}`);
                    toast.error("Not quite! Find a stronger move.");
                }
            } else {
                logWrongAttempt(`Illegal move ${from} to ${to}`);
                toast.error("Move is illegal in chess rules.");
            }
        } catch (e) {
            logWrongAttempt(`Illegal move ${from} to ${to}`);
            toast.error("Invalid move!");
        }
    };

    // Coordinate highlights for rendering pieces
    const renderBoardPieces = () => {
        if (activeTask.type === 'collect_items') {
            return { [piecePos]: `w${activeTask.piece[0]}` };
        } else if (activeTask.type === 'safe_squares') {
            const board = parseFenToBoard(activeTask.fen);
            board[piecePos] = 'wK';
            return board;
        } else if (activeTask.type === 'real_chess') {
            return parseFenToBoard(chessFen);
        } else if (activeTask.type === 'board_click') {
            return parseFenToBoard(activeTask.fen);
        }
        return {};
    };

    const boardPieces = renderBoardPieces();

    // Helper for coordinates text styling inside grid cells
    const boardTheme = myProfile?.boardTheme || 'green';
    const pieceTheme = myProfile?.pieceTheme || 'cburnett';
    const currentPieceSet = PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett;
    const { light: lightSquareColor, dark: darkSquareColor } = BOARD_THEMES[boardTheme] || BOARD_THEMES.green;
    const lightSq = boardTheme === 'neumorphic' ? 'var(--neu-inner)' : lightSquareColor;
    const darkSq = boardTheme === 'neumorphic' ? 'rgba(0,0,0,0.06)' : darkSquareColor;

    return (
        <div className="glass-panel animate-fade-in" style={{padding: '30px', border: '1px solid var(--border-glass)', borderRadius: '20px', background: 'var(--glass-bg)', color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 'var(--panel-shadow)' }}>
            
            {/* Header bar */}
            <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
                <button onClick={onBack} className="neu-btn" style={{padding: '10px 20px', fontWeight: 'bold' }}>⬅ Back</button>
                <div style={{fontSize: '20px', fontWeight: '800', color: '#3b82f6', textAlign: 'center' }}>
                    {classObj.icon} Class {classId}: {classObj.name}
                </div>
                <div style={{padding: '8px 16px', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '10px', fontWeight: 'bold', fontSize: '14px' }}>
                    Task {taskIndex + 1} / 15
                </div>
            </div>

            {/* Progress bar */}
            <div style={{width: '100%', background: 'var(--bg-secondary)', height: '10px', borderRadius: '10px', overflow: 'hidden', marginBottom: '35px', border: '1px solid var(--border-glass)' }}>
                <div style={{ width: `${completed ? 100 : (taskIndex / 15) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6 0%, #10b981 100%)', transition: 'width 0.3s ease-out' }}></div>
            </div>

            {completed ? (
                <div style={{textAlign: 'center', padding: '40px', background: 'rgba(16, 185, 129, 0.1)', border: '2px solid #10b981', borderRadius: '20px', width: '100%', maxWidth: '450px' }}>
                    <div style={{fontSize: '70px', marginBottom: '15px', animation: 'bounce 2s infinite' }}>🎓🏆</div>
                    <h2 style={{color: '#10b981', fontSize: '32px', fontWeight: 'bold', margin: '0 0 10px 0' }}>
                        {assignment.assignmentDocId && assignment.assignmentDocId !== 'INDIVIDUAL' ? "Mission Completed! 🚀" : "Class Mastered!"}
                    </h2>
                    <p style={{color: 'var(--text-secondary)', fontSize: '16px', marginBottom: '25px' }}>
                        {assignment.assignmentDocId && assignment.assignmentDocId !== 'INDIVIDUAL' 
                            ? "Excellent work! Your homework mission report has been submitted to the coach." 
                            : "Excellent work! You successfully solved all 15 chess challenges for this class."}
                    </p>
                    <button onClick={onBack} className="neu-btn-primary" style={{width: '100%' }}>
                        {assignment.assignmentDocId && assignment.assignmentDocId !== 'INDIVIDUAL' ? "Return to Quests" : "Continue Path"}
                    </button>
                </div>
            ) : (
                <div style={{display: 'flex', gap: '30px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start', width: '100%' }}>
                    
                    {/* Chess Board Grid container */}
                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ width: '100%', maxWidth: `${boardWidth}px`, background: 'rgba(0,0,0,0.2)', padding: '12px 18px', borderRadius: '8px', border: '1px solid var(--border-glass)', fontSize: '16px', fontWeight: 'bold', textAlign: 'center', color: '#3b82f6' }}>
                            {activeTask.question}
                        </div>
                        
                        <div ref={boardRef}
                             style={{ 
                                 width: `${boardWidth}px`, 
                                 maxWidth: '100%', 
                                 aspectRatio: '1/1', 
                                 border: '4px solid var(--border-glass)', 
                                 borderRadius: '12px', 
                                 overflow: 'hidden', 
                                 boxShadow: '0 10px 30px rgba(0,0,0,0.1)', 
                                 position: 'relative', 
                                 touchAction: 'none',
                                 userSelect: 'none',
                                 WebkitUserSelect: 'none'
                             }}>
                            <div style={{width: '100%', height: '100%', display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)' }}>
                                {ROWS.map((row, rIdx) => COLS.map((col, cIdx) => {
                                    const id = `${col}${row}`;
                                    const isDark = (rIdx + cIdx) % 2 === 1;
                                    const isSelected = selectedSquare === id;
                                    const isTarget = activeTask.type === 'board_click' && activeTask.clickTargets?.includes(id) && wrongAttempts >= 3;
                                    const isLegalDot = legalMoves.includes(id);

                                    // Check item (apple / star) drawing
                                    const hasApple = activeTask.type === 'collect_items' && applesOnBoard.includes(id);

                                    return (
                                        <div 
                                            key={id}
                                            onClick={() => handleSquareClick(id)}
                                            style={{position: 'relative',
                                                background: isSelected ? 'rgba(59,130,246,0.3)' : isTarget ? 'rgba(16,185,129,0.3)' : (isDark ? darkSq : lightSq),
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }}
                                        >
                                            {/* Coordinate tags */}
                                            {cIdx === 0 && <span style={{position: 'absolute', top: 2, left: 3, fontSize: '10px', color: 'var(--text-secondary)', opacity: 0.5, pointerEvents: 'none' }}>{row}</span>}
                                            {rIdx === 7 && <span style={{position: 'absolute', bottom: 1, right: 3, fontSize: '10px', color: 'var(--text-secondary)', opacity: 0.5, pointerEvents: 'none' }}>{col}</span>}
                                            
                                            {/* Legal moves dot highlight */}
                                            {isLegalDot && <div style={{width: '25%', height: '25%', background: 'rgba(16,185,129,0.6)', borderRadius: '50%', pointerEvents: 'none', zIndex: 5 }} />}
                                            
                                            {/* Apple/star drawing */}
                                            {hasApple && <div style={{ fontSize: `${boardWidth / 11}px`, zIndex: 4, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))', animation: 'bounce 2s infinite' }}>{activeTask.itemIcon || '🍎'}</div>}
                                        </div>
                                    );
                                }))}
                            </div>

                            {/* Render absolute-positioned interactive pieces */}
                            {Object.entries(boardPieces).map(([id, piece]) => {
                                const cIdx = COLS.indexOf(id[0]);
                                const rIdx = ROWS.indexOf(parseInt(id[1]) || id[1]);
                                const isMyPiece = piece.startsWith('w');
                                const isBeingDragged = dragSq === id;

                                return (
                                    <div
                                        key={id}
                                        draggable={false}
                                        onDragStart={e => e.preventDefault()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isDraggingRef.current) {
                                                handleSquareClick(id);
                                            }
                                        }}
                                        style={{
                                            position: 'absolute',
                                            width: '12.5%',
                                            height: '12.5%',
                                            left: isBeingDragged ? `${dragPos.x - pointerDownOffsetRef.current.x}px` : `${cIdx * 12.5}%`,
                                            top: isBeingDragged ? `${dragPos.y - pointerDownOffsetRef.current.y}px` : `${rIdx * 12.5}%`,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            cursor: isMyPiece ? 'grab' : 'default',
                                            zIndex: isBeingDragged ? 100 : 10,
                                            transition: (activeTask.type === 'collect_items' && !isBeingDragged) ? 'left 0.2s, top 0.2s' : 'none',
                                            touchAction: 'none',
                                            userSelect: 'none',
                                            WebkitUserSelect: 'none'
                                        }}
                                        onPointerDown={e => {
                                            if (isMyPiece) handlePointerDown(e, id);
                                            else handleSquareClick(id);
                                        }}
                                    >
                                        <img 
                                            src={currentPieceSet[piece]} 
                                            alt={piece} 
                                            draggable={false}
                                            style={{width: '90%', 
                                                height: '90%', 
                                                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))',
                                                pointerEvents: 'none',
                                                userSelect: 'none'
                                            }} 
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* MCQ Options list or hint panels */}
                    <div style={{flex: 1, minWidth: '300px', maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        
                        {activeTask.type === 'mcq' && (
                            <div className="glass-panel" style={{padding: '20px', border: '1px solid var(--border-glass)', background: 'rgba(0,0,0,0.1)', borderRadius: '12px' }}>
                                <h3 style={{marginTop: 0, fontSize: '18px', color: 'var(--text-primary)' }}>Select the Correct Answer:</h3>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px' }}>
                                    {activeTask.options.map((opt, i) => {
                                        const isAnswered = mcqAnswered === opt;
                                        const isCorrect = opt === activeTask.answer;
                                        return (
                                            <button
                                                key={i}
                                                onClick={() => handleMcqClick(opt)}
                                                className="neu-btn"
                                                style={{width: '100%',
                                                    padding: '14px',
                                                    fontSize: '15px',
                                                    textAlign: 'left',
                                                    background: isAnswered ? (isCorrect ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)') : 'var(--btn-bg)',
                                                    borderColor: isAnswered ? (isCorrect ? '#10b981' : '#ef4444') : 'var(--border-glass)',
                                                    color: 'var(--text-primary)',
                                                    borderRadius: '10px',
                                                    transition: '0.2s'
                                                }}
                                            >
                                                {opt}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="glass-panel" style={{padding: '20px', border: '1px solid var(--border-glass)', background: 'rgba(0,0,0,0.1)', borderRadius: '12px' }}>
                            <h3 style={{marginTop: 0, fontSize: '18px' }}>How to Play 💡</h3>
                            <ul style={{color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', paddingLeft: '20px', margin: '10px 0 0 0' }}>
                                <li>You can <strong>Tap/Click</strong> a piece and then click the target square to move.</li>
                                <li>You can also <strong>Drag and Drop</strong> the piece to your desired square.</li>
                                <li>For direct board clicks, simply click the square matching the instruction.</li>
                                <li>Wrong attempts will trigger sound warnings, and are saved in the logs.</li>
                            </ul>
                        </div>
                    </div>

                </div>
            )}

            <style>{`
                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-8px); }
                }
            `}</style>

        </div>
    );
}
